import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowLeft, ShoppingCart, Briefcase, User, FolderKanban, Lightbulb, Link2, Plus, Check, X, ChevronRight } from 'lucide-react';
interface CoreListItem {
  id: string;
  text: string;
  done: boolean;
  tags?: string[];
  time: string;
}
interface CoreList {
  id: string;
  title: string;
  icon: React.FC<{
    size?: number;
    className?: string;
  }>;
  items: CoreListItem[];
  filterTags: string[];
}
const CORE_LISTS: CoreList[] = [{
  id: 'shopping',
  title: 'Shopping List',
  icon: ShoppingCart,
  filterTags: ['Groceries', 'Home', 'Office', 'Tech'],
  items: [{
    id: 's1',
    text: 'Oat milk',
    done: false,
    tags: ['Groceries'],
    time: 'today'
  }, {
    id: 's2',
    text: 'Sourdough bread',
    done: false,
    tags: ['Groceries'],
    time: 'today'
  }, {
    id: 's3',
    text: 'USB-C hub for desk',
    done: false,
    tags: ['Tech'],
    time: 'yesterday'
  }, {
    id: 's4',
    text: 'Desk lamp',
    done: true,
    tags: ['Office'],
    time: '2 days ago'
  }, {
    id: 's5',
    text: 'Coffee beans',
    done: true,
    tags: ['Groceries'],
    time: '3 days ago'
  }]
}, {
  id: 'work',
  title: 'Work',
  icon: Briefcase,
  filterTags: ['Urgent', 'Follow-up', 'Meeting', 'Review', 'Deploy'],
  items: [{
    id: 'w1',
    text: 'Review Acme contract section 4.2',
    done: false,
    tags: ['Review', 'Urgent'],
    time: 'today'
  }, {
    id: 'w2',
    text: 'Approve Lisa\'s SSL cert PR',
    done: false,
    tags: ['Review', 'Deploy'],
    time: 'today'
  }, {
    id: 'w3',
    text: 'Reply to Sarah about Q2 budget',
    done: false,
    tags: ['Follow-up'],
    time: 'today'
  }, {
    id: 'w4',
    text: 'Schedule Acme partnership call',
    done: true,
    tags: ['Meeting'],
    time: 'yesterday'
  }, {
    id: 'w5',
    text: 'Send Paul privacy policy link',
    done: true,
    tags: ['Follow-up'],
    time: 'yesterday'
  }]
}, {
  id: 'personal',
  title: 'Personal',
  icon: User,
  filterTags: ['Health', 'Admin', 'Home', 'Family'],
  items: [{
    id: 'p1',
    text: 'Dentist appointment Thursday 4:30pm',
    done: false,
    tags: ['Health'],
    time: 'today'
  }, {
    id: 'p2',
    text: 'Renew car insurance',
    done: false,
    tags: ['Admin'],
    time: 'this week'
  }, {
    id: 'p3',
    text: 'Fix kitchen shelf',
    done: false,
    tags: ['Home'],
    time: 'this week'
  }, {
    id: 'p4',
    text: 'Call mum',
    done: true,
    tags: ['Family'],
    time: 'yesterday'
  }]
}, {
  id: 'projects',
  title: 'Projects',
  icon: FolderKanban,
  filterTags: ['Zenbot', 'TMA', 'Infrastructure', 'Research'],
  items: [{
    id: 'pr1',
    text: 'Zenbot UI redesign',
    done: false,
    tags: ['Zenbot'],
    time: 'active'
  }, {
    id: 'pr2',
    text: 'Nango sync pipeline',
    done: false,
    tags: ['Zenbot', 'Infrastructure'],
    time: 'active'
  }, {
    id: 'pr3',
    text: 'TMA patient onboarding',
    done: false,
    tags: ['TMA'],
    time: 'active'
  }, {
    id: 'pr4',
    text: 'Rate limiting RFC',
    done: false,
    tags: ['Infrastructure'],
    time: 'in review'
  }, {
    id: 'pr5',
    text: 'LiveKit voice quality research',
    done: true,
    tags: ['Zenbot', 'Research'],
    time: 'completed'
  }]
}, {
  id: 'ideas',
  title: 'Ideas',
  icon: Lightbulb,
  filterTags: ['Product', 'Tech', 'Business', 'Content'],
  items: [{
    id: 'i1',
    text: 'Voice-driven task creation via Zenbot',
    done: false,
    tags: ['Product'],
    time: 'today'
  }, {
    id: 'i2',
    text: 'Embed lists into Confluence pages',
    done: false,
    tags: ['Product', 'Tech'],
    time: 'yesterday'
  }, {
    id: 'i3',
    text: 'Weekly automated spend report',
    done: false,
    tags: ['Business'],
    time: 'this week'
  }, {
    id: 'i4',
    text: 'Blog post: building with LiveKit agents',
    done: false,
    tags: ['Content'],
    time: 'this week'
  }, {
    id: 'i5',
    text: 'Source rules for auto-categorizing invoices',
    done: false,
    tags: ['Product'],
    time: 'last week'
  }]
}, {
  id: 'links',
  title: 'Links',
  icon: Link2,
  filterTags: ['Read later', 'Reference', 'Tool', 'Article'],
  items: [{
    id: 'l1',
    text: 'LiveKit Agents SDK docs',
    done: false,
    tags: ['Reference', 'Tool'],
    time: 'today'
  }, {
    id: 'l2',
    text: 'Nango zero-YAML guide',
    done: false,
    tags: ['Reference'],
    time: 'yesterday'
  }, {
    id: 'l3',
    text: 'Mark Weiser: The Computer for the 21st Century',
    done: false,
    tags: ['Read later', 'Article'],
    time: 'this week'
  }, {
    id: 'l4',
    text: 'Tailwind v4 migration guide',
    done: false,
    tags: ['Reference'],
    time: 'this week'
  }, {
    id: 'l5',
    text: 'Hetzner ARM pricing comparison',
    done: true,
    tags: ['Reference'],
    time: 'last week'
  }]
}];
export function CoreListsTab({
  onSelect
}: {
  onSelect: (id: string) => void;
}) {
  return <div className="flex flex-col gap-3" data-id="element-680">
      {CORE_LISTS.map((list, i) => {
      const Icon = list.icon;
      return <motion.button key={list.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: i * 0.04
      }} onClick={() => onSelect(list.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-681">
            <div className="flex items-center gap-4" data-id="element-682">
              <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-683"><Icon size={18} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-684" /></div>
              <h2 className="flex-1 text-[#e8e4df] text-[15px] truncate" data-id="element-685">{list.title}</h2>
              <ChevronRight size={16} className="text-[#8a8680]/20 group-hover:text-[#8a8680]/60 transition-colors shrink-0" data-id="element-686" />
            </div>
          </motion.button>;
    })}
    </div>;
}
export function CoreListDetail({
  listId,
  onBack
}: {
  listId: string;
  onBack: () => void;
}) {
  const list = CORE_LISTS.find(l => l.id === listId);
  if (!list) return null;
  const [items, setItems] = useState(list.items);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const Icon = list.icon;
  const toggleDone = (id: string) => setItems(prev => prev.map(item => item.id === id ? {
    ...item,
    done: !item.done
  } : item));
  const removeItem = (id: string) => setItems(prev => prev.filter(item => item.id !== id));
  const toggleTag = (tag: string) => setActiveFilters(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const addItem = () => {
    if (!newItemText.trim()) return;
    setItems(prev => [{
      id: String(Date.now()),
      text: newItemText,
      done: false,
      tags: [],
      time: 'just now'
    }, ...prev]);
    setNewItemText('');
    setShowAdd(false);
  };
  const filtered = items.filter(item => {
    if (search && !item.text.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeFilters.length > 0 && item.tags && !item.tags.some(t => activeFilters.includes(t))) return false;
    return true;
  });
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-687">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-688">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-689"><ArrowLeft size={14} data-id="element-690" /><span className="text-[13px]" data-id="element-691">Lists</span></button>
        <header className="mb-8 flex items-center gap-3" data-id="element-692">
          <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-693"><Icon size={18} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-694" /></div>
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-695">{list.title}</h1>
        </header>

        <div className="relative mb-6" data-id="element-696">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-697" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-698" />
        </div>

        <div className="flex items-center gap-4 flex-wrap mb-6" data-id="element-699">
          {list.filterTags.map(tag => {
          const active = activeFilters.includes(tag);
          return <button key={tag} onClick={() => toggleTag(tag)} className={`text-[12px] transition-colors ${active ? 'text-[#4a9a9a]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-700">{tag}</button>;
        })}
        </div>

        <div className="flex flex-col" data-id="element-701">
          {filtered.map(item => <div key={item.id} className="flex items-center gap-3 py-3 group" data-id="element-702">
              <button onClick={() => toggleDone(item.id)} className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-all ${item.done ? 'bg-[#4a9a9a]/60 border-[#4a9a9a]/60' : 'border-[#8a8680]/30 hover:border-[#8a8680]/60'}`} data-id="element-703">
                {item.done && <Check size={9} className="text-[#141418]" data-id="element-704" />}
              </button>
              <span className={`flex-1 text-[15px] ${item.done ? 'line-through text-[#8a8680]/40' : 'text-[#e8e4df]/90'}`} data-id="element-705">{item.text}</span>
              <button onClick={() => removeItem(item.id)} className="text-[#8a8680]/0 group-hover:text-[#8a8680]/40 transition-opacity" data-id="element-706"><X size={13} data-id="element-707" /></button>
            </div>)}
          {filtered.length === 0 && <p className="text-[#8a8680]/40 text-[13px] py-8 text-center" data-id="element-708">Empty.</p>}
        </div>

        <div className="mt-6" data-id="element-709">
          {!showAdd ? <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-710"><Plus size={13} data-id="element-711" />Add</button> : <div className="flex items-center gap-3" data-id="element-712">
              <input type="text" value={newItemText} onChange={e => setNewItemText(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') addItem();
            if (e.key === 'Escape') setShowAdd(false);
          }} placeholder="New" className="flex-1 bg-transparent text-[#e8e4df] text-[14px] outline-none border-b border-[#8a8680]/20 pb-1 placeholder:text-[#8a8680]/30" autoFocus data-id="element-713" />
              <button onClick={addItem} className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-714">Add</button>
            </div>}
        </div>
      </div>
    </motion.div>;
}